/**
 * The Blue Book on reader surfaces (PRD v2 §5.16). One place decides which
 * sections are fit to show, in what order, under what label, and with what
 * attribution — the Pro/public detail card and the citizen modal both read it.
 * The state's text is never shown without its attribution (v1.9 §5.8.10).
 */

export interface BlueBookSectionView {
  section: string;
  body: string;
  page_reference?: string | null;
  display_order?: number | null;
}

export interface BlueBookSource {
  blue_book_sections?: BlueBookSectionView[] | null;
  blue_book_edition?: string | null;
  blue_book_entry_status?: string | null;
  blue_book_url?: string | null;
  /** The measure's cycle; its blue_book_url is the booklet every measure falls back to. */
  election_calendar?: { blue_book_url?: string | null } | null;
}

/**
 * The Blue Book link a reader sees: the measure's own link when it has one
 * (e.g. anchored to its page), otherwise its cycle's booklet — one link,
 * entered once on the election cycle. Mirrors the backend's measureBlueBookUrl.
 */
export const measureBlueBookUrl = (
  source: Pick<BlueBookSource, 'blue_book_url' | 'election_calendar'>
): string | null => source.blue_book_url || source.election_calendar?.blue_book_url || null;

/** "2026 Blue Book" — the reader-facing name of a cycle's booklet. */
export const cycleBlueBookLabel = (electionYear: number | null | undefined): string =>
  electionYear ? `${electionYear} Blue Book` : 'Blue Book';

/** Print order and reader-facing labels (the admin card's labels, minus the jargon). */
export const BLUE_BOOK_SECTION_LABELS: Record<string, string> = {
  summary: 'Summary and analysis',
  analysis: 'Analysis',
  vote_yes_means: 'What a YES vote means',
  vote_no_means: 'What a NO vote means',
  arguments_for: 'Arguments for',
  arguments_against: 'Arguments against',
  fiscal_estimate: 'Estimate of fiscal impact',
  background: 'Background',
  other: 'Additional information',
};

const PRINT_ORDER = Object.keys(BLUE_BOOK_SECTION_LABELS);

export const READABLE_BLUE_BOOK_STATUSES = ['entered', 'verified'] as const;

export const blueBookIsReadable = (entryStatus: string | null | undefined): boolean =>
  (READABLE_BLUE_BOOK_STATUSES as readonly string[]).includes(entryStatus ?? '');

/** The sections a reader may see, in print order — empty when the entry is not ready. */
export function readableBlueBookSections(source: BlueBookSource): BlueBookSectionView[] {
  if (!blueBookIsReadable(source.blue_book_entry_status)) return [];
  const sections = (source.blue_book_sections ?? []).filter((s) => s && s.body && s.body.trim());
  return [...sections].sort((a, b) => {
    const byOrder = (a.display_order ?? 0) - (b.display_order ?? 0);
    if (byOrder !== 0) return byOrder;
    return PRINT_ORDER.indexOf(a.section) - PRINT_ORDER.indexOf(b.section);
  });
}

export const blueBookSectionLabel = (section: string): string =>
  BLUE_BOOK_SECTION_LABELS[section] ?? section.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

/** "From the 2026 Blue Book (Legislative Council Staff)" — the attribution line. */
export const blueBookAttribution = (edition: string | null | undefined): string =>
  `From the ${edition?.trim() || 'Colorado Blue Book'}, the state's official ballot information booklet`;
